/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Directive } from '@angular/core';

import { AUTOMATION_GRID } from '@gh/config';
import { getClosest, isDomElement, isNotNil, logWarning } from '@gh/core-util';

import { GridApi, ProcessRowParams } from 'ag-grid-community';
import { isNil } from 'lodash';

import type { GridModel } from '../../state/grid-state.interfaces';
import {
  GRID_FEATURE,
  GridFeature,
  GridFeatureInitParams,
  GridFeatureReadyParams,
} from '../feature.interfaces';
import { GridOptionsExt } from './grid-options-ext';

/**
 * Extends grid capabilities
 */
@Directive({
  standalone: false,
  selector: 'gh-grid',
  providers: [{
    provide: GRID_FEATURE,
    useExisting: GridExtFeatureDirective,
    multi: true,
  }],
})
export class GridExtFeatureDirective implements GridFeature {
  // load this grid feature after all other grid features
  // this is necessary to wait until other features initialize getRowId, if any
  order = Number.MAX_SAFE_INTEGER;

  process: (params: ProcessRowParams) => void;

  private api: GridApi;
  private gridEl: Element;
  private bodyEl: Element;
  private lastPointer?: Element;

  constructor() {
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onAngularOverlayFocusOut = this.onAngularOverlayFocusOut.bind(this);
  }

  initGrid(params: GridFeatureInitParams): void {
    this.gridEl = params.nativeElement;
  }

  enhanceGridOptions(options: GridOptionsExt): void {
    if (AUTOMATION_GRID) {
      const { name, getRowId, getRowNodeAutomationId, noAutomation } = options;
      if (Object.prototype.hasOwnProperty.call(options, 'noAutomation') || Object.prototype.hasOwnProperty.call(options, 'getRowNodeAutomationId')) {
        delete options.noAutomation;
        delete options.getRowNodeAutomationId;
      }
      if (noAutomation) {
        return;
      }
      const contextModel = options.context as GridModel | undefined;
      const getAutomationId = getRowNodeAutomationId
        ?? (getRowId ? (data: any) => getRowId({ data } as any) : undefined)
        ?? (contextModel && typeof contextModel.getEntityId === 'function'
          ? (data: any) => String(contextModel.getEntityId(data))
          : undefined);
      if (isNil(getAutomationId)) {
        logWarning('GridExtFeature: automation: grid definition does not provide implementation of' +
          ' getRowId/getRowNodeAutomationId function');
        return;
      }

      this.process = (params: ProcessRowParams) => {
        const { data } = params.node;
        if (data && params.eRow) {
          const entityId = getAutomationId(data);
          if (isNil(entityId)) {
            logWarning('GridExtFeature: id is not defined (undefined or null) for entity in grid', data);
          }

          const automationId = name ? `${name}:row:${entityId}` : `row:${entityId}`;
          params.eRow.setAttribute('data-automation-id', automationId);
          params.ePinnedLeftRow?.setAttribute('data-automation-id', automationId);
          params.ePinnedRightRow?.setAttribute('data-automation-id', automationId);
        }
      };
    }
  }

  onGridReady(params: GridFeatureReadyParams): void {
    this.api = params.api;
    this.bodyEl = this.gridEl.querySelector('.ag-body-viewport')!;
    this.bodyEl.addEventListener('focusout', this.onFocusOut);
  }

  onDestroy(): void {
    this.cleanAngularOverlayFocusOut();
    this.bodyEl.removeEventListener('focusout', this.onFocusOut);
  }

  /**
   * This method completes current editing if grid loses focus.
   *
   * Implementation of this method was copied from ag-grid repository (gridPanel.ts).
   * We copied this functionality in order to support @angular/material overlay.
   * @param event
   */
  private onFocusOut(event: FocusEvent): void {
    const related = event.relatedTarget as Node | null;
    if (related == null) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.completeEditingIfFocusLeftGrid(event, document.activeElement));
      });
      return;
    }
    this.completeEditingIfFocusLeftGrid(event, related);
  }

  private completeEditingIfFocusLeftGrid(event: FocusEvent, elementWithFocus: Node | null): void {
    const active = document.activeElement;
    if (active instanceof HTMLElement && active.closest('.ag-popup-editor')) {
      return;
    }

    let clickInsideGrid = false;
    let ignoreClickOutsideGrid = false;
    let isAngularOverlay: boolean = false;
    let angularOverlayElement: Maybe<HTMLElement>;
    let pointer: any = elementWithFocus;

    while (isNotNil(pointer) && !clickInsideGrid) {

      const isPopup = isDomElement(pointer) && !!(pointer as HTMLElement).closest('.ag-popup-editor');
      if (isDomElement(pointer)) {
        isAngularOverlay = pointer.classList.contains('grid-overlay-panel');
        angularOverlayElement = pointer;
      }
      const isBody = this.bodyEl === pointer;

      clickInsideGrid = isPopup || isBody || isAngularOverlay;

      pointer = pointer.parentNode;
    }

    if (!clickInsideGrid) {
      const overlayElementForTarget = getClosest(event.target, 'grid-overlay-panel');
      if (overlayElementForTarget && overlayElementForTarget.classList.contains('grid-ignore-click-outside')) {
        ignoreClickOutsideGrid = true;
        isAngularOverlay = true;
        angularOverlayElement = overlayElementForTarget;
      }
    }

    if (isAngularOverlay) {
      this.listenAngularOverlayFocusOut(angularOverlayElement!);
    }

    if (!clickInsideGrid && !ignoreClickOutsideGrid) {
      this.api.stopEditing();
    }
  }

  private onAngularOverlayFocusOut(event: FocusEvent): void {
    this.cleanAngularOverlayFocusOut();
    this.onFocusOut(event);
  }

  /**
   * Listens for focusout event from given Angular overlay
   * @param pointer
   */
  private listenAngularOverlayFocusOut(pointer: Element): void {
    this.cleanAngularOverlayFocusOut();

    pointer.addEventListener('focusout', this.onAngularOverlayFocusOut);
    this.lastPointer = pointer;
  }

  /**
   * Cleans focusout listener for current angular overlay
   */
  private cleanAngularOverlayFocusOut(): void {
    if (this.lastPointer) {
      this.lastPointer.removeEventListener('focusout', this.onAngularOverlayFocusOut);
    }
  }
}
